import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import type { Permissions } from '@/lib/rbac';
import {
  SALES_AGENT_PERMISSIONS,
  isOperationsRole,
  OPERATIONS_STATUS_MODULES,
  ACCOUNTING_ROLE,
} from '@/lib/rbac';

// Mensaje canónico de denegación de escritura. Lo devuelven todos los servicios
// de mutación cuando el usuario autenticado no tiene el permiso `write` activo.
export const WRITE_PERMISSION_DENIED =
  'No tienes permisos de escritura para realizar esta acción.';

// Mensaje canónico de denegación de borrado. Lo devuelven los servicios que
// eliminan registros cuando el usuario no tiene el permiso `delete` activo.
export const DELETE_PERMISSION_DENIED =
  'No tienes permisos para eliminar este registro.';

export interface ResolvedProfile {
  role: string;
  permissions: Permissions;
}

// Resuelve el perfil del usuario autenticado (rol + permisos JSONB) consultando
// `app_users` -> `roles`. Devuelve null si no hay sesión o no se puede resolver.
// La resolución usa el auth.uid, con fallback por email (red de seguridad).
export async function resolveCurrentPermissions(): Promise<ResolvedProfile | null> {
  if (!isSupabaseConfigured || !supabase) return null;

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const user = sessionData.session?.user;
    if (!user) return null;

    let roleId: string | null = null;

    const byId = await supabase
      .from('app_users')
      .select('role_id')
      .eq('id', user.id)
      .maybeSingle();
    roleId = (byId.data as { role_id: string | null } | null)?.role_id ?? null;

    if (!roleId && user.email) {
      const byEmail = await supabase
        .from('app_users')
        .select('role_id')
        .eq('email', user.email)
        .maybeSingle();
      roleId = (byEmail.data as { role_id: string | null } | null)?.role_id ?? null;
    }

    if (!roleId) return null;

    const { data: role } = await supabase
      .from('roles')
      .select('name, permissions')
      .eq('id', roleId)
      .maybeSingle();

    if (!role) return null;

    const r = role as { name: string | null; permissions: Permissions | null };
    return { role: r.name ?? '', permissions: r.permissions ?? {} };
  } catch {
    return null;
  }
}

// Verifica si el usuario autenticado tiene permiso de ESCRITURA sobre un módulo.
// El rol `admin` tiene acceso total (red de seguridad del plan maestro).
// El rol `sales_agent` usa su whitelist explícita (inventario/comisiones de solo lectura).
export async function canWrite(module: string): Promise<boolean> {
  const profile = await resolveCurrentPermissions();
  if (!profile) return false;
  if (profile.role === 'admin') return true;
  if (profile.role === 'sales_agent') return Boolean(SALES_AGENT_PERMISSIONS[module]?.write);
  return Boolean(profile.permissions[module]?.write);
}

// Verifica si el usuario autenticado puede CAMBIAR EL ESTADO de un registro
// del módulo. A diferencia de `canWrite`, el rol de operaciones puede cambiar
// estados (en pedidos/producción/post-venta) sin tener `write` en la matriz.
export async function canChangeStatus(module: string): Promise<boolean> {
  const profile = await resolveCurrentPermissions();
  if (!profile) return false;
  if (profile.role === 'admin') return true;
  if (isOperationsRole(profile.role)) return OPERATIONS_STATUS_MODULES.has(module);
  return Boolean(profile.permissions[module]?.write);
}

// Verifica si el usuario autenticado tiene permiso de ELIMINAR sobre un módulo.
// Separado de `canWrite` para que el borrado exija el permiso `delete` (más
// restrictivo) y no solo `write`. El rol `admin` tiene acceso total.
// El agente de ventas nunca elimina registros.
export async function canDelete(module: string): Promise<boolean> {
  const profile = await resolveCurrentPermissions();
  if (!profile) return false;
  if (profile.role === 'admin') return true;
  if (profile.role === 'sales_agent') return false;
  return Boolean(profile.permissions[module]?.delete);
}

// Verifica si el usuario puede registrar abonos / cambiar el estado de pago de
// un PROVEEDOR. Es un permiso especial (como el cambio de estado de
// operaciones): el rol de contabilidad puede hacerlo sin tener `write` sobre el
// módulo `suppliers` (para que no pueda crear/editar/eliminar proveedores).
export async function canManageSupplierPayments(): Promise<boolean> {
  const profile = await resolveCurrentPermissions();
  if (!profile) return false;
  if (profile.role === 'admin') return true;
  if (profile.role === ACCOUNTING_ROLE) return true;
  return Boolean(profile.permissions['suppliers']?.write);
}